/**
 * The v2 editing lane's wire shapes (slate-feature-transfer W6 §B).
 *
 * Four envelope conventions meet in this one wave, which is why these live together:
 * `api/v2/documents/*` and `api/ai-actions/*` answer **bare** objects, `selection-actions`
 * answers `{data}`, and `suggestions.mention` answers `{pagination, data:{documents,users}}`.
 * Each schema below is the shape AFTER the adapter has unwrapped its envelope.
 */

import { z } from "zod";
import { ActorSchema, DocumentSchema } from "~/api/types";

// ── The lane itself (`api/v2/documents`) ─────────────────────────────────────

export const EditorLaneSchema = z.enum(["v1", "v2"]);
export type EditorLane = z.infer<typeof EditorLaneSchema>;

/**
 * `GET /api/v2/documents/{id}`. `stateV2` is a base64 Yjs update — the seed the engine applies
 * before the room's own history arrives, so a document is readable before the socket opens.
 */
export const EditorV2LaneSchema = z.object({
  lane: EditorLaneSchema,
  room: z.string().nullable().default(null),
  stateV2: z.string().nullable().default(null),
  contentV2: z.string().nullable().default(null),
  textV2: z.string().nullable().default(null),
  optedIntoV2At: z.string().nullable().default(null),
});
export type EditorV2Lane = z.infer<typeof EditorV2LaneSchema>;

/**
 * `POST /api/v2/documents/{id}/opt-in`. Non-destructive: the v1 `state`/`content`/`text` are
 * left intact and copied forward, and the v1 state is frozen as the rollback baseline.
 */
export const EditorV2OptInSchema = z.object({
  lane: EditorLaneSchema,
  room: z.string().nullable().default(null),
  contentV2: z.string().nullable().default(null),
  textV2: z.string().nullable().default(null),
});
export type EditorV2OptIn = z.infer<typeof EditorV2OptInSchema>;

/**
 * `POST /api/v2/documents/{id}/ai-action` — the dual-ACL choke point.
 *
 * The server does NOT downgrade an `act` intent it will not allow: it answers **403**. The
 * adapter turns that one status into `null` (not a thrown error) because the caller's correct
 * response is to re-ask on the propose lane, and an exception there would read as a failure.
 */
export const AiParticipantSchema = z.object({
  kind: z.enum(["ai", "human"]),
  id: z.string(),
  displayName: z.string(),
  model: z.string().nullable().default(null),
});

export const AiActionDecisionSchema = z.object({
  allowed: z.boolean(),
  lane: z.enum(["act", "propose"]),
  capability: z.string(),
  room: z.string().nullable().default(null),
  participant: AiParticipantSchema,
  range: z.object({ from: z.number(), to: z.number() }),
});
export type AiActionDecision = z.infer<typeof AiActionDecisionSchema>;

export interface AiActionRequest {
  capability: string;
  from: number;
  to: number;
  requestedLane?: "act" | "propose";
  thinkerId?: string;
  thinkerDisplayName?: string;
  model?: string;
}

export const EditorV2CommitSchema = z.object({
  committed: z.boolean(),
  room: z.string().nullable().default(null),
  range: z.object({ from: z.number(), to: z.number() }),
  by: z.string(),
});
export type EditorV2Commit = z.infer<typeof EditorV2CommitSchema>;

/** A card the document's thinker precipitated from a committed span. */
export const DerivedCardSchema = z.object({ kind: z.string(), statement: z.string() });
export type DerivedCard = z.infer<typeof DerivedCardSchema>;

export const DeriveOutcomeSchema = z.object({
  cards: z.array(DerivedCardSchema).default([]),
});
export type DeriveOutcome = z.infer<typeof DeriveOutcomeSchema>;

// ── Selection transform / refine (`api/ai-actions`) ──────────────────────────

export interface TransformRequest {
  documentId?: string;
  collectionId?: string;
  instructions: string;
  selectedText: string;
  documentTitle?: string;
  thinkerId?: string;
  idempotencyKey?: string;
}

/**
 * `transform-selection` answers **200 with the text** OR **202 with a dispatch id** when the
 * resolved thinker is an IDE pin — a queued round-trip through somebody's editor window, not
 * an error. Modelling that as a union is the difference between "the AI is thinking in Bob's
 * VS Code" and a spurious failure toast.
 */
export type TransformOutcome =
  | {
      kind: "text";
      text: string;
      thinkerId: string | null;
      thinkerName: string | null;
      model: string | null;
      source: string | null;
      tokensUsed: number | null;
    }
  | { kind: "dispatch"; dispatchId: string; thinkerId: string | null; thinkerName: string | null };

export const TransformTextSchema = z.object({
  transformedText: z.string(),
  thinkerId: z.string().nullable().default(null),
  thinkerName: z.string().nullable().default(null),
  model: z.string().nullable().default(null),
  source: z.string().nullable().default(null),
  tokensUsed: z.number().nullable().default(null),
});

export const TransformDispatchSchema = z.object({
  dispatchId: z.string(),
  thinkerId: z.string().nullable().default(null),
  thinkerName: z.string().nullable().default(null),
});

/** One poll of an IDE-pinned dispatch. `clipped` means the window returned more than 4000 chars. */
export const IdeDispatchSchema = z.object({
  dispatchId: z.string(),
  statusDetail: z.string(),
  queuedBehind: z.number().default(0),
  ownerUserName: z.string().nullable().default(null),
  thinkerId: z.string().nullable().default(null),
  thinkerName: z.string().nullable().default(null),
  transformedText: z.string().nullable().default(null),
  clipped: z.boolean().default(false),
  error: z.string().nullable().default(null),
});
export type IdeDispatch = z.infer<typeof IdeDispatchSchema>;

/** The states a dispatch can settle in. Anything else means "still queued — poll again". */
export const DISPATCH_TERMINAL = new Set(["completed", "failed", "cancelled", "expired"]);

export interface RefineRequest {
  originalText: string;
  currentResult: string;
  followUpMessage: string;
  conversationHistory?: Array<{ role: string; content: string }>;
  documentId?: string;
  collectionId?: string;
  thinkerId?: string;
}

/**
 * A frame of `refine-selection`'s `text/event-stream`. `thought` is the model reasoning the
 * drawer streams into its "AI Thinking" section; `delta` appends to the running result;
 * `result` is the whole final text. The stream terminates on a literal `data: [DONE]`.
 */
export type RefineEvent =
  | { type: "thought"; text: string }
  | { type: "delta"; text: string }
  | { type: "result"; text: string }
  | { type: "error"; message: string };

// ── Mentions (`suggestions.mention`) ─────────────────────────────────────────

export const MentionSuggestionsSchema = z.object({
  documents: z.array(DocumentSchema).default([]),
  users: z.array(ActorSchema).default([]),
});
export type MentionSuggestions = z.infer<typeof MentionSuggestionsSchema>;

// ── Live widget values (`pulse/quotes.get`) ──────────────────────────────────

export const QuoteSchema = z.object({
  symbol: z.string(),
  price: z.number().nullable().default(null),
  dayChangePct: z.number().nullable().default(null),
  currency: z.string().default(""),
  /** The SERVER's timestamp. Stamping the browser clock here would date a value we did not take. */
  asOf: z.string().nullable().default(null),
});
export type Quote = z.infer<typeof QuoteSchema>;

/**
 * Always HTTP 200. `available:false` with an empty list is DATA — "no quote provider is
 * configured" — and must never be surfaced as an error.
 */
export const QuoteSnapshotSchema = z.object({
  available: z.boolean(),
  quotes: z.array(QuoteSchema).default([]),
});
export type QuoteSnapshot = z.infer<typeof QuoteSnapshotSchema>;

/** The most symbols the server will accept in one call; it silently truncates beyond this. */
export const QUOTE_SYMBOL_LIMIT = 50;

// ── Scoped selection actions (`GET /api/selection-actions?…`) ────────────────

export interface ScopedActionQuery {
  teamId?: string;
  projectId?: string;
  documentId?: string;
}
